import React, { useEffect, useState } from "react";
import { useAuth } from "../firebase/auth.tsx";
import { useNavigate, useLocation } from "react-router-dom";
import apiFetch from "./helpers/requests";
import { useTranslation } from "react-i18next";
import { getPasswordErrors } from "./helpers/helpers.tsx";

import "../App.css";

const Verification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole, user, repeatVerification, setSession } = useAuth();
  
  const [resendText, setResendText] = useState(true);
  const [showTimer, setShowTimer] = useState(false);
  const [timer, setTimer] = useState(
    parseInt(localStorage.getItem("timer") || "60")
  );
  
  // States for the new password flow
  const [oobCode, setOobCode] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");
  
  const { t } = useTranslation();

  // Listen for verification completion in another tab
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // When storage changes and verificationCompleted is updated, redirect
      if (e.key === "verificationCompleted") {
        navigate("/main");
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [navigate]);

  const resendEmail = () => {
    repeatVerification();
    setResendText(false);
    setShowTimer(true);

    const interval = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer === 1) {
          clearInterval(interval);
          setShowTimer(false);
          setResendText(true);
          localStorage.removeItem("timer");
          return 60;
        } else {
          localStorage.setItem("timer", String(prevTimer - 1));
          return prevTimer - 1;
        }
      });
    }, 1000);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get("oobCode");

    if (code) {
      setOobCode(code);
    } else if (userRole || !user) {
      // If no oobCode and user is already completely verified/role fetched, or not logged in
      navigate("/main");
      return;
    }

    if (!code) {
      const storedTimer = parseInt(localStorage.getItem("timer") || "60");
      if (storedTimer < 60) {
        setTimer(storedTimer);
        setResendText(false);
        setShowTimer(true);
        const interval = setInterval(() => {
          setTimer((prevTimer) => {
            if (prevTimer === 1) {
              clearInterval(interval);
              setShowTimer(false);
              setResendText(true);
              localStorage.removeItem("timer");
              return 60;
            } else {
              localStorage.setItem("timer", String(prevTimer - 1));
              return prevTimer - 1;
            }
          });
        }, 1000);
      }
    }
  }, [location, navigate, user, userRole]);

  const handleVerifyAndLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const passwordError = getPasswordErrors(password, confirmPassword, t);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    try {
      setIsVerifying(true);
      setError("");

      const result = await apiFetch(`/verifyUserByCode`, {
        method: "POST",
        body: JSON.stringify({ oobCode, newPassword: password }),
      }, true);

      if (result.error) {
        setError(result.error);
        setIsVerifying(false);
        return;
      }

      // Notify other tabs that verification is done
      localStorage.setItem("verificationCompleted", Date.now().toString());

      // Auto login user using the session data returned from backend
      setSession(result);

    } catch (err) {
      console.error("Email verification error:", err);
      setError(t("There_was_an_unspecified_error_Please_try_again"));
      setIsVerifying(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "calc(100vh - 40px)",
        paddingTop: "40px",
        maxWidth: "600px",
        margin: "0 auto",
      }}
    >
      <div className="w-100 m-4">
        <img
          src="/images/VR_ICU_Logo_Main.png"
          alt="Logo"
          className="mb-3"
          style={{
            width: "calc(20vh - 20px)",
            position: "absolute",
            top: "3vh",
            left: "7vh",
          }}
        />
        
        {!oobCode && (
          // Pending Verification state (Timer, Resend Email)
          <>
            <h4 style={{ textAlign: "center" }}>
              {t("Please_verify_your_email")}
            </h4>
            <div style={{ textAlign: "center" }}>
              <label
                style={{
                  marginLeft: "20px",
                  marginTop: "20px",
                  color: "#404040",
                  fontFamily: "VirtualLabQuickSandMedium",
                  fontSize: "1.4em",
                }}
              >
                {t(
                  "Once_you_verify_your_email_address_you_can_get_started_in_VR_ICU"
                )}
              </label>
            </div>
            <div style={{ textAlign: "center" }}>
              <label
                style={{
                  marginLeft: "20px",
                  marginTop: "12vh",
                  color: "#404040",
                  fontFamily: "VirtualLabQuickSandMedium",
                  fontSize: "1.4em",
                }}
              >
                {t("Didnt_receive_an_email")}?{" "}
                {resendText ? (
                  <span
                    style={{
                      color: "#404040",
                      fontWeight: "bold",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={resendEmail}
                  >
                    {t("Resend_email")}
                  </span>
                ) : (
                  <span style={{ color: "#44963C", fontWeight: "bold" }}>
                    {t("Email_sent")}
                  </span>
                )}
              </label>
            </div>
            <div style={{ textAlign: "center" }}>
              <label
                htmlFor="emailInput"
                style={{
                  marginLeft: "20px",
                  color: "#404040",
                  fontFamily: "VirtualLabQuickSandMedium",
                  fontSize: "1.4em",
                  visibility: showTimer ? "visible" : "hidden",
                }}
              >
                {t("Wait")} <span style={{ fontWeight: "bold" }}>{timer}</span>{" "}
                {timer !== 1 ? t("seconds") : t("second")}{" "}
                {t("to_resend_another_email")}
              </label>
            </div>
          </>
        )}

        {oobCode && (
          // Create New Password state (Email verification link clicked)
          <>
            <h4 style={{ textAlign: "center", marginBottom: "20px" }}>
              {t("Your_email_has_been_verified")}
            </h4>
            <div style={{ textAlign: "center", marginBottom: "30px" }}>
              <label
                style={{
                  color: "#404040",
                  fontFamily: "VirtualLabQuickSandMedium",
                  fontSize: "1.2em",
                }}
              >
                {t("Enter_New_Password")}
              </label>
            </div>

            <form onSubmit={handleVerifyAndLogin} className="m-4">
              <div className="mb-3" style={{ marginTop: "2vh" }}>
                <label
                  htmlFor="passwordInput"
                  style={{
                    marginLeft: "20px",
                    color: "#404040",
                  }}
                >
                  {t("Password")}
                </label>
                <input
                  type="password"
                  className="form-control"
                  id="passwordInput"
                  placeholder={t("Minimum_6_characters")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{
                    backgroundColor: "#CCCCCC",
                    borderRadius: "20px",
                    color: "#404040",
                    height: "5vh",
                    maxHeight: "40px",
                    fontSize: "1em",
                    paddingLeft: "20px",
                  }}
                />
              </div>
              <div className="mb-3">
                <input
                  type="password"
                  className="form-control"
                  id="passwordConfirmInput"
                  placeholder={t("Confirm_password")}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={{
                    backgroundColor: "#CCCCCC",
                    borderRadius: "20px",
                    color: "#404040",
                    height: "5vh",
                    maxHeight: "40px",
                    fontSize: "1em",
                    paddingLeft: "20px",
                  }}
                />
              </div>
              {error && (
                <div style={{ textAlign: "center", color: "#A63939", fontSize: "0.8em", marginBottom: "1vh" }}>
                  {error}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  marginTop: "3vh",
                }}
              >
                <button
                  type="submit"
                  className="btn btn-primary mb-2"
                  disabled={isVerifying}
                  style={{
                    width: "45%",
                    height: "5vh",
                    maxHeight: "40px",
                    maxWidth: "100%",
                    borderRadius: "20px",
                    marginTop: "10px",
                    backgroundColor: "#39A686",
                    borderColor: "#39A686",
                    color: "white",
                  }}
                >
                  {isVerifying ? t("Verifying") + "..." : t("Confirm")}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Verification;
